import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Page, PageHeader } from "@/components/PageHeader";
import { money, timeAgo, tierColor, sentimentColor, stageLabel, stageColor } from "@/lib/format";
import { MagnifyingGlass, Crown } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GuestAvatar } from "@/components/GuestAvatar";

const STAGES = [
  { key: "all", label: "All" },
  { key: "lead", label: "Leads" },
  { key: "upcoming", label: "Upcoming" },
  { key: "inhouse", label: "In-house" },
  { key: "past", label: "Past guests" },
  { key: "lost", label: "No-show / Canceled" },
];

export default function Guests() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [tier, setTier] = useState("all");
  const [sentiment, setSentiment] = useState("all");
  const [stage, setStage] = useState("all");

  const { data: stats = {} } = useQuery({
    queryKey: ["guest-stats"],
    queryFn: async () => (await api.get("/guests/stats")).data,
  });

  const { data: guests = [], isLoading } = useQuery({
    queryKey: ["guests", search, tier, sentiment, stage],
    queryFn: async () =>
      (await api.get("/guests", { params: { search, tier, sentiment, stage: stage === "all" ? undefined : stage } })).data,
  });

  return (
    <Page>
      <PageHeader title="Guests" subtitle="Unified contacts — leads and guests — consolidated from PMS, mailbox, reviews, chat & surveys.">
        <span className="text-sm text-muted-foreground">{guests.length} profiles</span>
      </PageHeader>

      {/* stage pills */}
      <div className="flex flex-wrap gap-2 mb-4">
        {STAGES.map((s) => {
          const count = stats[s.key];
          const active = stage === s.key;
          return (
            <button
              key={s.key}
              data-testid={`stage-filter-${s.key}`}
              onClick={() => setStage(s.key)}
              className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-secondary"}`}
            >
              {s.label}
              {count != null && <span className={`ml-1.5 text-xs ${active ? "opacity-90" : "text-muted-foreground"}`}>{count}</span>}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2.5 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="guest-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search guests by name or email…"
            className="w-full h-10 pl-9 pr-3 rounded-md border border-border bg-card text-sm outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <Select value={tier} onValueChange={setTier}>
          <SelectTrigger className="w-[150px] h-10" data-testid="filter-tier"><SelectValue placeholder="Tier" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tiers</SelectItem>
            <SelectItem value="Bronze">Bronze</SelectItem>
            <SelectItem value="Silver">Silver</SelectItem>
            <SelectItem value="Gold">Gold</SelectItem>
            <SelectItem value="Platinum">Platinum</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sentiment} onValueChange={setSentiment}>
          <SelectTrigger className="w-[160px] h-10" data-testid="filter-sentiment"><SelectValue placeholder="Sentiment" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sentiment</SelectItem>
            <SelectItem value="positive">Positive</SelectItem>
            <SelectItem value="neutral">Neutral</SelectItem>
            <SelectItem value="negative">Negative</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary/50 text-left text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
              <th className="font-semibold px-4 py-3">Guest</th>
              <th className="font-semibold px-4 py-3">Stage</th>
              <th className="font-semibold px-4 py-3 hidden md:table-cell">Tier</th>
              <th className="font-semibold px-4 py-3 hidden md:table-cell">Stays</th>
              <th className="font-semibold px-4 py-3 text-right">Lifetime Value</th>
              <th className="font-semibold px-4 py-3 hidden lg:table-cell">Sentiment</th>
              <th className="font-semibold px-4 py-3 hidden lg:table-cell">Last Activity</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">Loading…</td></tr>}
            {!isLoading && guests.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">No guests match your filters.</td></tr>}
            {guests.map((g, i) => (
              <tr
                key={g.id}
                data-testid={`guest-row-${i}`}
                onClick={() => navigate(`/app/guests/${g.id}`)}
                className={`border-b border-border last:border-0 cursor-pointer hover:bg-secondary/50 transition-colors ${i % 2 ? "bg-secondary/20" : ""}`}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <GuestAvatar src={g.avatar} name={g.name} className="h-9 w-9 rounded-full border border-border" textClassName="text-xs font-semibold" />
                    <div>
                      <div className="font-medium flex items-center gap-1.5">
                        {g.name}
                        {g.vip && <Crown size={13} weight="fill" className="text-primary" />}
                      </div>
                      <div className="text-xs text-muted-foreground">{g.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {g.lifecycle_status && (
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${stageColor[g.lifecycle_status] || "bg-secondary border-border"}`}>
                      {stageLabel[g.lifecycle_status] || g.lifecycle_status}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 hidden md:table-cell"><span className={`text-[11px] px-2 py-0.5 rounded-full border ${tierColor[g.loyalty_tier]}`}>{g.loyalty_tier}</span></td>
                <td className="px-4 py-3 hidden md:table-cell tabular-nums">{g.total_stays}</td>
                <td className="px-4 py-3 text-right font-medium tabular-nums">{money(g.lifetime_value)}</td>
                <td className="px-4 py-3 hidden lg:table-cell"><span className={`text-[11px] px-2 py-0.5 rounded border ${sentimentColor[g.sentiment]}`}>{g.sentiment}</span></td>
                <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground text-xs">{timeAgo(g.last_activity)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Page>
  );
}
